import React, { useMemo, useEffect } from 'react';
import ReactFlow, {
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  Panel,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';

// Customer grouping — maps edge_node_id slugs to org nodes
const CUSTOMER_GROUPS = [
  {
    id: 'cps-energy',
    label: 'CPS Energy',
    memberIds: ['ingram-rd-substation', 'foster-rd-substation', 'scenic-loop-substation'],
  },
  {
    id: 'aligned-dc',
    label: 'Aligned Data Center',
    memberIds: ['aligned-data-center', 'lab-dc-prime'],
  },
];

const GROUPED_IDS = new Set(CUSTOMER_GROUPS.flatMap(g => g.memberIds));
const AGENT_SPACING = 240;
const GROUP_GAP = 120;

function timeAgo(iso) {
  if (!iso) return 'never';
  // Force UTC if backend omits timezone designator
  const ts = /[Z+]/.test(iso) ? iso : iso + 'Z';
  const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (diff <= 0) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const DISPLAY_NAME_OVERRIDES = {
  'lab-dc-prime': 'Lab DC Prime',
};
function displayName(id) {
  return DISPLAY_NAME_OVERRIDES[id] ??
    id.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// ── Node renderers ────────────────────────────────────────────────────────────

const CloudNode = () => (
  <div className="px-8 py-3 rounded-xl bg-slate-800/60 border border-sky-500/30"
    style={{ boxShadow: '0 0 24px rgba(56,189,248,0.08)', minWidth: 260 }}>
    <div className="flex items-center gap-3">
      <svg className="w-5 h-5 text-sky-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
      <div>
        <div className="text-sm font-semibold text-sky-300">EdgeNotary Cloud</div>
        <div className="text-[10px] text-slate-500">Management · Policy Engine · Intelligence</div>
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} style={{ background: 'transparent', border: 'none' }} />
  </div>
);

const CustomerNode = ({ data }) => {
  const onlineCount = data.onlineCount ?? 0;
  return (
    <div className="px-5 py-2.5 rounded-lg bg-slate-800/50 border border-violet-500/30"
      style={{ boxShadow: '0 0 16px rgba(139,92,246,0.07)', minWidth: 190 }}>
      <Handle type="target" position={Position.Top} style={{ background: 'transparent', border: 'none' }} />
      <div className="flex items-center gap-2">
        <svg className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <div className="text-xs font-semibold text-violet-300">{data.label}</div>
      </div>
      <div className="text-[9px] text-slate-500 mt-0.5 pl-5">
        {onlineCount} of {data.totalCount} online
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: 'transparent', border: 'none' }} />
    </div>
  );
};

const AgentNode = ({ data }) => {
  const { node } = data;
  const isOnline = node.status === 'Online' || node.status === 'Active';
  return (
    <div className={`rounded-xl border px-4 py-3 w-52 ${
      isOnline
        ? 'bg-slate-800/60 border-emerald-500/30'
        : 'bg-slate-800/40 border-slate-700/40'
    }`} style={{ boxShadow: isOnline ? '0 0 16px rgba(16,185,129,0.06)' : undefined }}>
      <Handle type="target" position={Position.Top} style={{ background: 'transparent', border: 'none' }} />
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
          isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'
        }`} />
        <div className="text-xs font-semibold text-slate-200 truncate">{displayName(node.edge_node_id)}</div>
      </div>
      <div className="flex flex-wrap gap-1 pl-4">
        <span className={`text-[9px] px-1.5 py-0.5 rounded ${
          isOnline ? 'bg-emerald-900/40 text-emerald-400' : 'bg-slate-700/60 text-slate-500'
        }`}>
          {node.status || 'Offline'}
        </span>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-400">
          {node.assigned_policies?.length || 0} policies
        </span>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-400">
          {timeAgo(node.last_heartbeat)}
        </span>
      </div>
    </div>
  );
};

const NODE_TYPES = { cloudNode: CloudNode, customerNode: CustomerNode, agentNode: AgentNode };

// ── Layout engine ─────────────────────────────────────────────────────────────

export default function TopologyGlobal({ edgeNodes }) {
  const agents = edgeNodes || [];

  const { computedNodes, computedEdges } = useMemo(() => {
    const agentById = Object.fromEntries(agents.map(n => [n.edge_node_id, n]));
    const ungrouped = agents.filter(n => !GROUPED_IDS.has(n.edge_node_id));

    // Place groups left-to-right, accumulating xCursor
    let xCursor = 0;
    const groupLayouts = CUSTOMER_GROUPS.map(group => {
      const members = group.memberIds.map(id => agentById[id] ?? {
        edge_node_id: id,
        status: 'Offline',
        last_heartbeat: null,
        assigned_policies: [],
      });
      const width = members.length * AGENT_SPACING;
      const groupX = xCursor;
      const centerX = groupX + width / 2 - 95; // 190px node / 2
      xCursor += width + GROUP_GAP;
      return { ...group, members, groupX, centerX };
    });

    // Ungrouped agents placed after groups, directly under cloud
    const ungroupedStartX = xCursor;

    // Total canvas width for cloud centering
    const totalWidth = Math.max(
      xCursor - GROUP_GAP + (ungrouped.length > 0 ? ungrouped.length * AGENT_SPACING : 0),
      xCursor - GROUP_GAP
    );
    const cloudX = totalWidth / 2 - 130; // 260px cloud / 2

    const nodes = [
      {
        id: 'cloud',
        type: 'cloudNode',
        position: { x: Math.max(0, cloudX), y: 60 },
        data: {},
      },
    ];
    const edges = [];

    // ── Grouped agents ──
    groupLayouts.forEach(({ id, label, members, groupX, centerX }) => {
      const onlineCount = members.filter(m => m.status === 'Online' || m.status === 'Active').length;
      const anyOnline = onlineCount > 0;

      nodes.push({
        id: `group-${id}`,
        type: 'customerNode',
        position: { x: centerX, y: 220 },
        data: { label, totalCount: members.length, onlineCount },
      });

      edges.push({
        id: `cloud-to-group-${id}`,
        source: 'cloud',
        target: `group-${id}`,
        animated: anyOnline,
        style: {
          stroke: anyOnline ? '#8B5CF6' : '#475569',
          strokeWidth: 1.5,
          strokeDasharray: anyOnline ? undefined : '5 4',
        },
        label: anyOnline ? 'mgmt' : 'offline',
        labelStyle: { fill: anyOnline ? '#8B5CF666' : '#47556966', fontSize: 9 },
        labelBgStyle: { fill: 'transparent' },
      });

      members.forEach((node, i) => {
        const isOnline = node.status === 'Online' || node.status === 'Active';
        nodes.push({
          id: `agent-${node.edge_node_id}`,
          type: 'agentNode',
          position: { x: groupX + i * AGENT_SPACING, y: 390 },
          data: { node },
        });

        edges.push({
          id: `group-${id}-to-${node.edge_node_id}`,
          source: `group-${id}`,
          target: `agent-${node.edge_node_id}`,
          animated: isOnline,
          style: {
            stroke: isOnline ? '#10B981' : '#475569',
            strokeWidth: 1.5,
            strokeDasharray: isOnline ? undefined : '5 4',
          },
          label: isOnline ? 'active' : 'offline',
          labelStyle: { fill: isOnline ? '#10B98166' : '#47556966', fontSize: 9 },
          labelBgStyle: { fill: 'transparent' },
        });
      });
    });

    // ── Ungrouped agents — connected directly to cloud ──
    ungrouped.forEach((node, i) => {
      const isOnline = node.status === 'Online' || node.status === 'Active';
      nodes.push({
        id: `agent-${node.edge_node_id}`,
        type: 'agentNode',
        position: { x: ungroupedStartX + i * AGENT_SPACING, y: 220 },
        data: { node },
      });

      edges.push({
        id: `cloud-to-${node.edge_node_id}`,
        source: 'cloud',
        target: `agent-${node.edge_node_id}`,
        animated: isOnline,
        style: {
          stroke: isOnline ? '#10B981' : '#475569',
          strokeWidth: 1.5,
          strokeDasharray: isOnline ? undefined : '5 4',
        },
        label: isOnline ? 'mgmt tunnel' : 'offline',
        labelStyle: { fill: isOnline ? '#10B98166' : '#47556966', fontSize: 9 },
        labelBgStyle: { fill: 'transparent' },
      });
    });

    return { computedNodes: nodes, computedEdges: edges };
  }, [agents]);

  const [rfNodes, setNodes, onNodesChange] = useNodesState(computedNodes);
  const [rfEdges, setEdges, onEdgesChange] = useEdgesState(computedEdges);

  // Sync layout when edgeNodes data refreshes
  useEffect(() => { setNodes(computedNodes); }, [computedNodes, setNodes]);
  useEffect(() => { setEdges(computedEdges); }, [computedEdges, setEdges]);

  if (agents.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-slate-500 text-sm">No edge agents registered</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={NODE_TYPES}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        minZoom={0.2}
        maxZoom={2.0}
        nodesConnectable={false}
        proOptions={{ hideAttribution: true }}
      >
        <Panel position="top-left">
          <div className="flex flex-col gap-2 ml-1 mt-1">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/80 border border-violet-500/40 backdrop-blur-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
              <span className="text-xs font-semibold text-violet-300 tracking-wide">CPS Energy POV Demo</span>
            </div>
            <div className="flex items-center gap-3 px-3 py-1.5 rounded-lg bg-slate-800/70 border border-slate-700/50 backdrop-blur-sm">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-[10px] text-emerald-400 font-medium">Live</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400/50" />
                <span className="text-[10px] text-slate-500 font-medium">Replay</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-violet-400/50" />
                <span className="text-[10px] text-slate-500 font-medium">Simulated</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-500/50" />
                <span className="text-[10px] text-slate-500 font-medium">Observes. Never acts.</span>
              </div>
            </div>
          </div>
        </Panel>
        <Controls className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl" />
        <MiniMap
          nodeColor={(node) => {
            if (node.type === 'cloudNode') return '#38bdf8';
            if (node.type === 'customerNode') return '#8b5cf6';
            const isOnline = node.data?.node?.status === 'Online' || node.data?.node?.status === 'Active';
            return isOnline ? '#10b981' : '#475569';
          }}
          maskColor="rgba(15, 23, 42, 0.8)"
          className="!bg-slate-800/90 !border-slate-600/50 !rounded-xl"
        />
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="#1e293b" />
      </ReactFlow>
    </div>
  );
}
